import './FilterMenu.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { FilterDisplay } from './FilterDisplay';
import { FilterOutlined } from '@ant-design/icons';
import { getTitleWithCount } from '@sonar-software/sonar-ts-lib';
import { SonarText } from '@ui/text';
import { TableFilter } from '@ui/table-modifier';

import { Button, Drawer } from 'antd';
import { DynamicFilter, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { Else, If, Then } from 'react-if';
import React, { useCallback, useEffect, useMemo } from 'react';
import { UpdateQueryParams, useBoolean, usePrevious, useQueryParams } from '@hooks';

export interface FilterMenuProps {
	deferFiltering?: boolean;
	filters: Array<TableFilter>;
	filterParams: DynamicFilter & { filterCount?: number };
	sonarObjectId?: number | null;
	sonarObjectType?: SonarObjectType | null;
	updateFilterParams: UpdateQueryParams<DynamicFilter>;
}

export const FilterMenu: React.FC<FilterMenuProps> = ({
	deferFiltering,
	filters,
	filterParams,
	sonarObjectId,
	sonarObjectType,
	updateFilterParams
}) => {
	const [ open, setOpen ] = useBoolean();
	const prevFilterParams = usePrevious(filterParams, filterParams);

	const { queryParams, updateParams } = useQueryParams<DynamicFilter>({
		defaultParams: filterParams
	});

	useEffect(() => {
		if (prevFilterParams !== filterParams) {
			updateParams(filterParams);
		}
	}, [ filterParams, prevFilterParams, updateParams ]);

	const handleClose = useCallback(() => {
		if (deferFiltering) {
			updateFilterParams(queryParams);
		}

		setOpen.off();
	}, [ deferFiltering, queryParams, setOpen, updateFilterParams ]);

	const groups = useMemo(() => {
		if (!filters.some((filter) => Boolean(filter.filterGroup))) {
			return [];
		}

		return [ ...new Set(filters.map((filter) => filter.filterGroup)) ];
	}, [ filters ]);

	const hasGroups = Boolean(groups.length);

	function mapFilter(filter: TableFilter) {
		return (
			<FilterDisplay
				filter={ filter }
				filterParams={ deferFiltering ? queryParams : filterParams }
				key={ filter.filterId }
				sonarObjectId={ sonarObjectId }
				sonarObjectType={ sonarObjectType }
				updateFilterParams={ deferFiltering ? updateParams : updateFilterParams } />
		);
	}

	return (
		<>
			<Button
				className={ classNames('filter-menu-button', { active: Boolean(filterParams.filterCount) }) }
				icon={ <FilterOutlined /> }
				onClick={ setOpen.toggle }
				type='text'>
				{ getTitleWithCount(COPY_SPECIFIC.FILTERS, filterParams.filterCount) }
			</Button>
			<Drawer
				className='data-dictionary-filter-drawer'
				footer={ deferFiltering ?
					<Button
						onClick={ handleClose }
						type='primary'>
						{ COPY_SPECIFIC.APPLY_FILTERS }
					</Button> : undefined }
				mask={ Boolean(deferFiltering) }
				onClose={ handleClose }
				open={ open }
				title={
					<SonarText
						size={ 20 }
						weight='semibold'>
						{ COPY_SPECIFIC.ADD_FILTERS }
					</SonarText>
				}>
				<div className='filters'>
					<If condition={ hasGroups }>
						<Then>
							{ filters?.filter((filter) => !filter.filterGroup).map(mapFilter) }
							{ groups.map((group) =>
								<React.Fragment key={ group }>
									<SonarText
										size={ 16 }
										weight='semibold'>{ group }
									</SonarText>
									{ filters.filter((filter) => filter.filterGroup === group).map(mapFilter) }
								</React.Fragment>) }
						</Then>
						<Else>
							{ filters.map(mapFilter) }
						</Else>
					</If>
				</div>
			</Drawer>
		</>
	);
};
